"use client"

import { clx } from "@medusajs/ui"
import { useEffect, useRef, useState } from "react"

import { FacetGroup, FacetKey } from "@modules/store/components/facet-list"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"

const SORT_OPTIONS: { value: SortOptions; label: string }[] = [
  { value: "created_at", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
]

type FacetBarProps = {
  groups: FacetGroup[]
  onToggle: (key: FacetKey, value: string) => void
  onClearAll: () => void
  hasActive: boolean
  isOptionActive: (key: FacetKey, value: string) => boolean
  selectedCount: (key: FacetKey) => number
  totalSelected: number
  sortBy: SortOptions
  onSort: (value: SortOptions) => void
}

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className={clx("transition-transform", { "rotate-180": open })}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
)

const Check = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
)

const FacetBar = ({
  groups,
  onToggle,
  onClearAll,
  hasActive,
  isOptionActive,
  selectedCount,
  totalSelected,
  sortBy,
  onSort,
}: FacetBarProps) => {
  // Only one dropdown open at a time (by id); click-outside / Escape closes.
  const [openId, setOpenId] = useState<string | null>(null)
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!openId) return
    const onDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setOpenId(null)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenId(null)
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [openId])

  const activeGroups = groups.filter((g) => g.options.length > 0)
  const currentSort =
    SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? "Newest"
  const sortOpen = openId === "sort"

  return (
    <div
      ref={barRef}
      className="mb-6 flex flex-wrap items-center gap-2.5"
      data-testid="facet-bar"
    >
      {activeGroups.map((group) => {
        const id = `facet-${group.key}`
        const open = openId === id
        const count = selectedCount(group.key)
        return (
          <div key={group.key} className="relative">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpenId(open ? null : id)}
              className={clx(
                "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors",
                count > 0
                  ? "border-clay bg-clay/5 text-clay"
                  : "border-line bg-card text-ink hover:border-clay hover:text-clay"
              )}
            >
              <span className="font-medium">{group.label}</span>
              {count > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-clay px-1.5 text-xs font-semibold text-white">
                  {count}
                </span>
              )}
              <Chevron open={open} />
            </button>

            {open && (
              <div className="absolute left-0 top-full z-40 mt-2 max-h-80 w-64 overflow-y-auto rounded-xl border border-line bg-surface p-2 shadow-xl">
                {group.options.map((option) => {
                  const isActive = isOptionActive(group.key, option.value)
                  const isAvailable = option.count > 0 || isActive
                  return (
                    <button
                      key={option.value}
                      type="button"
                      disabled={!isAvailable}
                      aria-pressed={isActive}
                      onClick={() => onToggle(group.key, option.value)}
                      className={clx(
                        "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                        isAvailable
                          ? "hover:bg-card"
                          : "cursor-not-allowed opacity-40"
                      )}
                    >
                      <span
                        className={clx(
                          "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                          isActive
                            ? "border-clay bg-clay text-white"
                            : "border-line bg-card text-transparent"
                        )}
                      >
                        <Check />
                      </span>
                      <span
                        className={clx(
                          "flex-1 truncate",
                          isActive ? "font-medium text-ink" : "text-ink"
                        )}
                      >
                        {option.value}
                      </span>
                      <span className="text-xs text-muted">{option.count}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {hasActive && (
        <button
          type="button"
          onClick={onClearAll}
          className="inline-flex items-center text-sm font-medium text-ink underline underline-offset-4 transition-colors hover:text-clay"
        >
          Clear all{totalSelected > 0 ? ` (${totalSelected})` : ""}
        </button>
      )}

      {/* Sort — pushed to the right, same dropdown pattern (single-select). */}
      <div className="relative ml-auto">
        <button
          type="button"
          aria-expanded={sortOpen}
          onClick={() => setOpenId(sortOpen ? null : "sort")}
          className="inline-flex items-center gap-2 rounded-full border border-line bg-card px-4 py-2 text-sm text-ink transition-colors hover:border-clay hover:text-clay"
        >
          <span className="text-muted">Sort:</span>
          <span className="font-medium">{currentSort}</span>
          <Chevron open={sortOpen} />
        </button>

        {sortOpen && (
          <div className="absolute right-0 top-full z-40 mt-2 w-56 overflow-hidden rounded-xl border border-line bg-surface p-2 shadow-xl">
            {SORT_OPTIONS.map((option) => {
              const isActive = option.value === sortBy
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onSort(option.value)
                    setOpenId(null)
                  }}
                  className={clx(
                    "flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-card",
                    isActive ? "font-medium text-clay" : "text-ink"
                  )}
                >
                  {option.label}
                  {isActive && <Check />}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default FacetBar
